// /schemas/registration/registration.schema.js
import { z } from "zod";
import { msg } from "@/utils/multilingual";

/** ————— Common Threat Patterns ————— */
const INVISIBLE_RE = /[\u200B-\u200D\u2060\uFEFF]/; // zero-width
const CONTROL_RE = /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/; // control chars
const TAG_RE = /<[^>]*>/; // any HTML/XML tags
const ANGLE_RE = /[<>]/; // angle brackets
const EMOJI_RE = /\p{Extended_Pictographic}/u;

// Common SQLi markers
const SQLI_RE = new RegExp(
  String.raw`(--|;--|;|\bUNION\b\s+\bSELECT\b|\bSELECT\b.+\bFROM\b|\bINSERT\b\s+\bINTO\b|\bUPDATE\b\s+\w+\s+\bSET\b|\bDELETE\b\s+\bFROM\b|\bDROP\b\s+\bTABLE\b|\bSLEEP\s*\(|\bBENCHMARK\s*\(|\bINFORMATION_SCHEMA\b|\bOR\b\s+1\s*=\s*1)`,
  "i"
);

// NoSQL operator payloads
const NOSQL_TOKENS = [
  "$ne",
  "$gt",
  "$gte",
  "$lt",
  "$lte",
  "$in",
  "$nin",
  "$regex",
  "$where",
  "$expr",
  "$function",
  "$accumulator",
  "$lookup",
  "$project",
  "$group",
  "$match",
  "$out",
  "$merge",
];
const hasNoSqlTokens = (s) =>
  NOSQL_TOKENS.some((t) => s.toLowerCase().includes(t.toLowerCase()));

// Repeated chars / sequences
const TRIPLE_REPEAT_RE = /(.)\1{2,}/;
const hasExcessiveRepeats = (s) => {
  if (!s || s.length < 3) return false;
  const firstChar = s[0];
  return s
    .split("")
    .every((char) => char.toLowerCase() === firstChar.toLowerCase());
};

// Email regex
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// URL regex
const URL_RE = /^https?:\/\/.+/;

// E.164 phone format: +[1-9][0-9]{7,14}
const PHONE_E164_RE = /^\+[1-9]\d{7,14}$/;

/**
 * Safe text validation with multilingual error messages
 */
const createSafeTextSchema = (
  t,
  fieldKey,
  { maxLength = 200, allowEmoji = false, minLength = 1, required = true } = {}
) => {
  const baseStringSchema = z
    .string()
    .trim()
    .min(
      required ? minLength : 0,
      t(`registration.registration_form.validation.${fieldKey}.minLength`)
    )
    .max(
      maxLength,
      t(`registration.registration_form.validation.${fieldKey}.max`)
    )
    .refine(
      (v) => !v || !INVISIBLE_RE.test(v),
      t(
        `registration.registration_form.validation.${fieldKey}.invisibleChars`
      )
    )
    .refine(
      (v) => !v || !CONTROL_RE.test(v),
      t(`registration.registration_form.validation.${fieldKey}.controlChars`)
    )
    .refine(
      (v) => !v || !TAG_RE.test(v),
      t(`registration.registration_form.validation.${fieldKey}.htmlTags`)
    )
    .refine(
      (v) => !v || !ANGLE_RE.test(v),
      t(`registration.registration_form.validation.${fieldKey}.angleBrackets`)
    )
    .refine(
      (v) => !v || !SQLI_RE.test(v),
      t(`registration.registration_form.validation.${fieldKey}.sqlPattern`)
    )
    .refine(
      (v) => !v || !hasNoSqlTokens(v),
      t(`registration.registration_form.validation.${fieldKey}.noSqlPattern`)
    )
    .refine(
      (v) => !v || !TRIPLE_REPEAT_RE.test(v),
      t(
        `registration.registration_form.validation.${fieldKey}.excessiveRepeats`
      )
    )
    .refine(
      (v) => !v || !hasExcessiveRepeats(v),
      t(
        `registration.registration_form.validation.${fieldKey}.justRepeatedChars`
      )
    )
    .refine(
      (v) => !v || allowEmoji || !EMOJI_RE.test(v),
      t(`registration.registration_form.validation.${fieldKey}.emoji`)
    );

  if (required) {
    return baseStringSchema;
  }

  // For optional fields: accept string, null, undefined, or empty string
  // Transform empty/undefined to null, then validate if it's a string
  return z
    .union([z.string(), z.null(), z.undefined(), z.literal("")])
    .transform((v) => {
      if (v === undefined || v === null || v === "") return null;
      return v;
    })
    .pipe(
      z.union([
        z.null(), // null is allowed
        baseStringSchema, // if it's a string, validate it
      ])
    )
    .optional();
};

/**
 * Registration Schema
 */
export const getRegistrationSchema = (t = msg) =>
  z.object({
    firstName: createSafeTextSchema(t, "firstName", {
      maxLength: 100,
      required: true,
    }),
    lastName: createSafeTextSchema(t, "lastName", {
      maxLength: 100,
      required: true,
    }),
    email: z
      .string()
      .trim()
      .min(1, t("common.email_is_required"))
      .max(255, t("registration.registration_form.validation.email.max"))
      .refine(
        (v) => EMAIL_RE.test(v),
        t("registration.registration_form.validation.email.invalidFormat")
      )
      .refine(
        (v) => !INVISIBLE_RE.test(v),
        t("common.email_contains_invisible_characters")
      )
      .refine(
        (v) => !CONTROL_RE.test(v),
        t("common.email_contains_control_characters")
      )
      .refine(
        (v) => !SQLI_RE.test(v),
        t("registration.registration_form.validation.email.sqlPattern")
      )
      .refine(
        (v) => !hasNoSqlTokens(v),
        t("registration.registration_form.validation.email.noSqlPattern")
      ),
    phone: z
      .string()
      .trim()
      .optional()
      .refine(
        (val) => {
          // Allow empty values
          if (!val || val.trim() === "") return true;
          // If provided, validate E.164 format: +[1-9][0-9]{7,14}
          return PHONE_E164_RE.test(val);
        },
        {
          message: t(
            "registration.registration_form.validation.phone.invalid"
          ),
        }
      ),
    hotelName: createSafeTextSchema(t, "hotelName", {
      maxLength: 200,
      required: true,
    }),
    hotelCity: createSafeTextSchema(t, "hotelCity", {
      maxLength: 128,
      required: true,
    }),
    hotelCityCode: z.string().trim().min(1).max(128).optional().default(""),
    hotelCountry: createSafeTextSchema(t, "hotelCountry", {
      maxLength: 128,
      required: true,
    }),
    hotelCountryCode: z.string().trim().min(1).max(10).optional().default(""),
    hotelState: z.string().trim().min(1).max(128).optional().default(""),
    hotelStateCode: z.string().trim().min(1).max(128).optional().default(""),
    website: z
      .union([
        z
          .string()
          .trim()
          .refine(
            (v) => !v || URL_RE.test(v),
            t(
              "registration.registration_form.validation.website.invalidFormat"
            )
          )
          .refine(
            (v) => !v || !INVISIBLE_RE.test(v),
            t(
              "registration.registration_form.validation.website.invisibleChars"
            )
          )
          .refine(
            (v) => !v || !CONTROL_RE.test(v),
            t(
              "registration.registration_form.validation.website.controlChars"
            )
          )
          .refine(
            (v) => !v || !SQLI_RE.test(v),
            t("registration.registration_form.validation.website.sqlPattern")
          )
          .refine(
            (v) => !v || !hasNoSqlTokens(v),
            t(
              "registration.registration_form.validation.website.noSqlPattern"
            )
          ),
        z.literal(""),
      ])
      .optional()
      .default(""),
    message: createSafeTextSchema(t, "message", {
      maxLength: 2000,
      required: false,
    })
      .optional()
      .default(""),
  });
